import React from 'react';
import { BellOff } from 'lucide-react';
import { useChatContext } from 'stream-chat-react';
import { useChatPresence } from '../../../context/ChatPresenceContext';
import ChatAvatar from '../parts/ChatAvatar';
import { SHARE_TYPES, describeAttachment, firstName, formatListStamp } from '../utils/chatFormat';
import styles from '../Chat.module.css';

const previewOf = (message) => {
  if (!message) return 'No messages yet';
  if (message.type === 'deleted' || message.deleted_at) return 'Message deleted';
  const attachments = message.attachments || [];
  if (attachments.some((a) => a.type === 'pbi_filter_share' || a.type === 'native_report_share')) return 'Shared a report view';
  if (attachments.some((a) => a.type === 'ai_session_share')) return 'Shared an AI conversation';
  if (message.text) return message.text;
  const files = attachments.filter((a) => !SHARE_TYPES.includes(a.type)).map(describeAttachment);
  if (!files.length) return 'Message';
  if (files.length > 1) return `${files.length} files`;
  return files[0].kind === 'image' ? 'Photo' : files[0].name;
};

const CustomChannelListItem = ({ channel, isActive, onClick, channelName }) => {
  const { client } = useChatContext();
  const { getUserOnlineStatus } = useChatPresence();
  const me = client?.user?.id;

  const members = Object.values(channel?.state?.members || {});
  const others = members.filter((m) => m.user?.id !== me);
  const isGroup = members.length > 2;
  const otherUser = others.length === 1 ? others[0].user : null;
  const online = otherUser ? getUserOnlineStatus(otherUser.id).online : false;

  const messages = channel?.state?.messages || [];
  const last = messages[messages.length - 1];
  let preview = previewOf(last);
  if (last?.user && (isGroup || last.user.id === me) && !(last.type === 'deleted')) {
    preview = `${last.user.id === me ? 'You' : firstName(last.user)}: ${preview}`;
  }

  const unread = channel?.countUnread?.() || 0;
  const muted = channel?.muteStatus?.().muted || false;
  const stamp = formatListStamp(last?.created_at || channel?.state?.last_message_at);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${styles.channelItem} ${isActive ? styles.active : ''} ${unread > 0 ? styles.unread : ''}`}
      aria-current={isActive ? 'true' : undefined}
    >
      <ChatAvatar
        name={channelName}
        id={isGroup ? channel?.id : otherUser?.id}
        image={!isGroup ? otherUser?.image : null}
        group={isGroup}
        online={online}
      />
      <span className={styles.itemBody}>
        <span className={styles.itemRow}>
          <span className={styles.itemName} title={channelName}>{channelName}</span>
          {muted && <BellOff size={12} className={styles.mutedIcon} aria-label="Muted" />}
          <span className={styles.itemTime}>{stamp}</span>
        </span>
        <span className={styles.itemRow} style={{ alignItems: 'center' }}>
          <span className={styles.itemPreview}>{preview}</span>
          {unread > 0 && (
            <span className={styles.unreadBadge} aria-label={`${unread} unread`}>{unread > 99 ? '99+' : unread}</span>
          )}
        </span>
      </span>
    </button>
  );
};

export default CustomChannelListItem;
